/**
 * The pieces the Wireline & Slickline AI pages draw (WL-6, fieldiq-api#1362):
 * the deterministic figures of each run as rows, the AI draft with its review,
 * and the "Draft with AI" launchers the day sheet, ticket, job and report
 * pages carry. WS-6's WsAiBits, for this vertical — the same layout and review
 * rules over WL's figures (a day sheet's hours, runs, footage, shots, plugs
 * and sources; a preflight's unpriced lines and run kinds with no rate; crew
 * certificates, pressure control, the stack check, lines, gauges, sources and
 * dose).
 *
 * THE FIGURES ARE THE API'S DETERMINISTIC LAYER, shown first and in full. The
 * AI wrote only the draft text below them. A pending draft reads "awaiting
 * your approval" and says nothing is published. A run whose narrative is
 * absent still has correct figures, and says the narrative is absent with the
 * API's reason.
 *
 * Every value that did not arrive renders `—`; a real 0 renders `0`. Depths
 * and footage arrive in the tenant's units and are shown as sent.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Pencil, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useReviewWlAi, useRunWlAi, useWlAiDigest, type WlAiRunRequest } from '../../../hooks/useWlAi';
import { wlWithUnit } from '../../../lib/wireline-slickline';
import { wlDay, wlHours } from '../../../lib/wireline-slickline-jobs';
import { WL_AI_MAX_NARRATIVE, WL_AI_MAX_OBSERVATION, wlAiRefusal, wlAiStateSkin } from '../../../lib/wl-ai';
import type {
  WlAiDraft,
  WlAiRunResult,
  WlComplianceDigestData,
  WlPreflightData,
  WlReportDraftData,
  WlRunKindGap,
  WlSheetSummaryData,
} from '../../../types/wl-ai';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlBadge, WlEmpty, WlField, WlFormError, WlPanel, WlSpec } from '../WlBits';

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const text = (v: unknown): string => (typeof v === 'string' && v.trim() ? v : '—');
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

// ─── A small read-only list of rows ──────────────────────────────────────────

interface Col<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

function AiTable<T>({ rows, cols, rowKey, rail, empty, testId, minWidth = 'min-w-[640px]' }: {
  rows: T[];
  cols: Col<T>[];
  rowKey: (row: T, i: number) => string;
  rail?: (row: T) => string;
  empty: string;
  testId: string;
  minWidth?: string;
}) {
  if (rows.length === 0) return <WlEmpty>{empty}</WlEmpty>;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${minWidth} text-left`} data-testid={testId}>
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} data-testid={`${testId}-row`} className={`border-t border-l-4 border-fieldiq-border ${rail ? rail(r) : 'border-l-transparent'}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const days = (list: unknown) => {
  const d = arr<string>(list);
  return d.length ? d.map((x) => wlDay(x)).join(', ') : '—';
};

/** A boolean the API computed, as words; absent → `—`. */
const yesNo = (v: unknown, yes: string, no: string): string => (v === true ? yes : v === false ? no : '—');

// ─── Day-sheet summary ───────────────────────────────────────────────────────

export function SheetSummaryFigures({ data }: { data: WlSheetSummaryData }) {
  const s = data.sheet ?? null;
  const kinds = arr<WlSheetSummaryData['runsByKind'][number]>(data.runsByKind);
  const runs = arr<WlSheetSummaryData['runs'][number]>(data.runs);
  const gaps = arr<WlSheetSummaryData['timeline']['gaps'][number]>(data.timeline?.gaps);
  const misfires = arr<WlSheetSummaryData['misfireWarnings'][number]>(data.misfireWarnings);
  const plugs = arr<WlSheetSummaryData['plugs'][number]>(data.plugs);
  const sources = arr<WlSheetSummaryData['sources'][number]>(data.sources);
  const crew = arr<WlSheetSummaryData['crew'][number]>(data.crew);
  const tests = arr<WlSheetSummaryData['pressureTests'][number]>(data.pressureTests);
  const drills = arr<WlSheetSummaryData['drills'][number]>(data.drills);
  const L = data.lengthUnit ?? null;
  const title = [s?.jobNumber, s?.workDate ? wlDay(s.workDate) : null].filter((p) => p && p !== '—').join(' · ') || 'Day-sheet summary';
  return (
    <div className="space-y-4" data-testid="wl-ai-sheet-figures">
      <WlPanel eyebrow="Day-sheet summary" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WlSpec label="Job" value={s?.jobId ? <Link className="underline" to={`/wireline-slickline/jobs/${s.jobId}`}>{text(s.jobNumber)}</Link> : '—'} mono />
          <WlSpec label="Day sheet" value={s?.id ? <Link className="underline" to={`/wireline-slickline/sheets/${s.id}`} data-testid="wl-ai-sheet-link">{wlDay(s.workDate)}</Link> : '—'} />
          <WlSpec label="Customer" value={text(s?.customerName)} />
          <WlSpec label="Well" value={text(s?.wellName)} />
          <WlSpec label="Unit" value={text(s?.unitNumber)} />
          <WlSpec label="Sheet" value={s?.startTime || s?.endTime ? `${text(s?.startTime)} → ${text(s?.endTime)}` : '—'} />
          <WlSpec label="Sheet hours" value={wlHours(num(data.hours?.sheet))} mono />
          <WlSpec label="Inside runs" value={wlHours(num(data.hours?.insideRuns))} mono />
          <WlSpec label="Standby" value={wlHours(num(data.hours?.standby))} mono />
          <WlSpec label="Travel" value={wlHours(num(data.hours?.travel))} mono />
          <WlSpec label="Crew hours" value={wlHours(num(data.hours?.crew))} mono />
          <WlSpec label="Crew travel" value={wlHours(num(data.hours?.crewTravel))} mono />
          <WlSpec label="Runs" value={displayValue(runs.length)} mono />
          <WlSpec label="Footage" value={wlWithUnit(num(data.footage), L)} mono />
          <WlSpec label="Deepest" value={wlWithUnit(num(data.deepest), data.depthUnit ?? null)} mono />
          <div className="min-w-0" data-testid="wl-ai-rig-up-test">
            <WlSpec label="Rig-up pressure test" value={yesNo(data.rigUpTestMissing, 'Not recorded', 'Recorded')} />
          </div>
        </dl>
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Runs by kind" testId="wl-ai-sheet-kinds">
        <AiTable
          rows={kinds}
          testId="wl-ai-kind"
          rowKey={(k) => k.kind}
          empty="No run was logged on this sheet."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'kind', header: 'Kind', render: (k) => <span className="font-semibold text-white">{text(k.label)}</span> },
            { key: 'runs', header: 'Runs', align: 'right', render: (k) => displayValue(num(k.runs)) },
            { key: 'footage', header: 'Footage', align: 'right', render: (k) => wlWithUnit(num(k.footage), L) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Runs" testId="wl-ai-sheet-runs">
        <AiTable
          rows={runs}
          testId="wl-ai-run"
          rowKey={(r, i) => `${r.runNumber}:${i}`}
          empty="No run was logged on this sheet."
          cols={[
            { key: 'n', header: 'Run', align: 'right', render: (r) => displayValue(num(r.runNumber)) },
            { key: 'kind', header: 'Kind', render: (r) => text(r.kind) },
            { key: 'up', header: 'Rig up', render: (r) => text(r.rigUp) },
            { key: 'down', header: 'Rig down', render: (r) => text(r.rigDown) },
            { key: 'depth', header: 'Max depth', align: 'right', render: (r) => text(r.maxDepth) },
            { key: 'footage', header: 'Footage', align: 'right', render: (r) => text(r.footage) },
            { key: 'result', header: 'Result', render: (r) => text(r.result) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Gaps in the timeline" testId="wl-ai-sheet-gaps">
        <AiTable
          rows={gaps}
          testId="wl-ai-gap"
          rowKey={(g, i) => `${g.from}:${i}`}
          rail={() => 'border-l-fi-amber'}
          empty="The runs and logged time cover the whole sheet."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'from', header: 'From', render: (g) => text(g.from) },
            { key: 'to', header: 'To', render: (g) => text(g.to) },
            { key: 'minutes', header: 'Minutes', align: 'right', render: (g) => displayValue(num(g.minutes)) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Perforating" testId="wl-ai-sheet-shots">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-4">
          <WlSpec label="Guns" value={displayValue(num(data.shots?.guns))} mono />
          <WlSpec label="Shots loaded" value={displayValue(num(data.shots?.loaded))} mono />
          <WlSpec label="Shots fired" value={displayValue(num(data.shots?.fired))} mono />
          <WlSpec label="Not fired" value={displayValue(num(data.shots?.derivedMisfires))} mono />
        </dl>
        {misfires.length > 0 && (
          <AiTable
            rows={misfires}
            testId="wl-ai-misfire"
            rowKey={(w, i) => `${w.runNumber}:${w.gunPosition}:${i}`}
            rail={() => 'border-l-fi-amber'}
            empty=""
            minWidth="min-w-[480px]"
            cols={[
              { key: 'run', header: 'Run', align: 'right', render: (w) => displayValue(num(w.runNumber)) },
              { key: 'gun', header: 'Gun', align: 'right', render: (w) => displayValue(num(w.gunPosition)) },
              { key: 'recorded', header: 'Misfires recorded', align: 'right', render: (w) => displayValue(num(w.misfireCount)) },
              { key: 'derived', header: 'Loaded less fired', align: 'right', render: (w) => displayValue(num(w.derivedMisfires)) },
            ]}
          />
        )}
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Plugs, stops, logs and sources" testId="wl-ai-sheet-downhole">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <WlSpec label="Survey stops" value={displayValue(num(data.surveyStops))} mono />
          <WlSpec label="Log intervals" value={displayValue(num(data.logIntervals))} mono />
        </dl>
        <AiTable
          rows={plugs}
          testId="wl-ai-plug"
          rowKey={(p, i) => `${p.runNumber}:${i}`}
          empty="No plug was set or pulled on this sheet."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'run', header: 'Run', align: 'right', render: (p) => displayValue(num(p.runNumber)) },
            { key: 'action', header: 'Action', render: (p) => text(p.action) },
            { key: 'kind', header: 'Plug', render: (p) => text(p.kind) },
            { key: 'depth', header: 'Depth', align: 'right', render: (p) => text(p.depth) },
          ]}
        />
        {sources.length > 0 && (
          <AiTable
            rows={sources}
            testId="wl-ai-source"
            rowKey={(x, i) => `${x.runNumber}:${i}`}
            empty=""
            minWidth="min-w-[360px]"
            cols={[
              { key: 'run', header: 'Run', align: 'right', render: (x) => displayValue(num(x.runNumber)) },
              { key: 'isotope', header: 'Source', render: (x) => text(x.isotope) },
              { key: 'serial', header: 'Serial', render: (x) => text(x.serialNumber) },
            ]}
          />
        )}
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Crew" testId="wl-ai-sheet-crew">
        <AiTable
          rows={crew}
          testId="wl-ai-crew"
          rowKey={(c, i) => `${c.name}:${i}`}
          empty="No crew was logged on this sheet."
          minWidth="min-w-[480px]"
          cols={[
            { key: 'name', header: 'Name', render: (c) => <span className="font-semibold text-white">{text(c.name)}</span> },
            { key: 'position', header: 'Position', render: (c) => text(c.position) },
            { key: 'hours', header: 'Hours', align: 'right', render: (c) => wlHours(num(c.hours)) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Day-sheet summary" title="Pressure tests and drills" testId="wl-ai-sheet-tests">
        <AiTable
          rows={tests}
          testId="wl-ai-pressure-test"
          rowKey={(x, i) => `${x.kind}:${i}`}
          empty="No pressure test was logged on this sheet."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'kind', header: 'Test', render: (x) => text(x.kind) },
            { key: 'on', header: 'Tested', render: (x) => wlDay(x.testedOn) },
            { key: 'result', header: 'Result', render: (x) => text(x.result) },
          ]}
        />
        {drills.length > 0 && (
          <AiTable
            rows={drills}
            testId="wl-ai-drill"
            rowKey={(x, i) => `${x.kind}:${i}`}
            empty=""
            minWidth="min-w-[420px]"
            cols={[
              { key: 'kind', header: 'Drill', render: (x) => text(x.kind) },
              { key: 'on', header: 'Drilled', render: (x) => wlDay(x.drilledOn) },
              { key: 'secs', header: 'Seconds to secure', align: 'right', render: (x) => displayValue(num(x.secondsToSecure)) },
            ]}
          />
        )}
      </WlPanel>
      <WlPanel eyebrow="From the day sheet" title="Remarks">
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{text(data.remarks)}</p>
      </WlPanel>
    </div>
  );
}

// ─── Preflight ───────────────────────────────────────────────────────────────

const READY_SKIN = { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const NOT_READY_SKIN = { label: 'Not ready', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

export function PreflightFigures({ data }: { data: WlPreflightData }) {
  const unpriced = arr<WlPreflightData['unpricedLines'][number]>(data.unpricedLines);
  const sheets = arr<WlPreflightData['sheetsWithoutTicket'][number]>(data.sheetsWithoutTicket);
  const kinds = arr<WlRunKindGap>(data.runKindsWithoutRate);
  const title = data.ticket?.ticketNumber || (data.job?.jobNumber ? `${data.job.jobNumber} (whole job)` : 'Preflight');
  return (
    <div className="space-y-4" data-testid="wl-ai-preflight-figures">
      <WlPanel eyebrow="Preflight" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ready to send</dt>
            <dd className="mt-1" data-testid="wl-ai-preflight-ready">
              {typeof data.ready === 'boolean' ? <WlBadge skin={data.ready ? READY_SKIN : NOT_READY_SKIN} value={String(data.ready)} /> : '—'}
            </dd>
          </div>
          <div className="min-w-0" data-testid="wl-ai-preflight-flags">
            <WlSpec label="Flags" value={displayValue(num(data.flagCount))} mono />
          </div>
          <WlSpec label="Tickets checked" value={displayValue(num(data.ticketsChecked))} mono />
          <WlSpec label="Sheets checked" value={displayValue(num(data.sheetsChecked))} mono />
          <WlSpec label="Currency" value={text(data.currency)} />
        </dl>
      </WlPanel>
      <WlPanel eyebrow="Preflight" title="Unpriced lines" testId="wl-ai-unpriced">
        <AiTable
          rows={unpriced}
          testId="wl-ai-unpriced-line"
          rowKey={(l) => l.lineId}
          rail={() => 'border-l-fi-amber'}
          empty="Every line has a price."
          cols={[
            { key: 'ticket', header: 'Ticket', render: (l) => (l.ticketId ? <Link className="font-mono underline" to={`/wireline-slickline/tickets/${l.ticketId}`}>{text(l.ticketNumber)}</Link> : '—') },
            { key: 'line', header: 'Line', render: (l) => <span className="font-semibold text-white">{text(l.description)}</span> },
            { key: 'why', header: 'Why', render: (l) => text(l.reason) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Preflight" title="Approved day sheets on no ticket" testId="wl-ai-unticketed">
        <AiTable
          rows={sheets}
          testId="wl-ai-unticketed-sheet"
          rowKey={(s) => s.sheetId}
          rail={() => 'border-l-fi-amber'}
          empty="Every approved day sheet on this job is on a ticket."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'day', header: 'Work date', render: (s) => <Link className="underline" to={`/wireline-slickline/sheets/${s.sheetId}`}>{wlDay(s.workDate)}</Link> },
            { key: 'unit', header: 'Unit', render: (s) => text(s.unitNumber) },
          ]}
        />
        {data.scope === 'ticket' && sheets.length > 0 && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted" data-testid="wl-ai-unticketed-note">
            These are the job's other sheets; they do not stop this ticket being sent.
          </p>
        )}
      </WlPanel>
      <WlPanel eyebrow="Preflight" title="Run kinds with no rate" testId="wl-ai-kind-gaps">
        <AiTable
          rows={kinds}
          testId="wl-ai-kind-gap"
          rowKey={(g) => g.runKind}
          rail={() => 'border-l-fi-amber'}
          empty="Every run kind on the sheets has a rate on the card."
          cols={[
            { key: 'label', header: 'Run kind', render: (g) => <span className="font-semibold text-white">{text(g.label)}</span> },
            { key: 'runs', header: 'Runs', align: 'right', render: (g) => displayValue(num(g.runs)) },
            { key: 'days', header: 'Days', render: (g) => days(g.days) },
          ]}
        />
      </WlPanel>
    </div>
  );
}

// ─── Service report draft ────────────────────────────────────────────────────

/** The report the narrative is for. The report's own figures live on the report itself. */
export function ReportDraftFigures({ data }: { data: WlReportDraftData }) {
  return (
    <WlPanel eyebrow="Service report draft" title="Service report" testId="wl-ai-report-figures">
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
        <WlSpec label="Job" value={data.job?.id ? <Link className="underline" to={`/wireline-slickline/jobs/${data.job.id}`}>{text(data.job.jobNumber)}</Link> : '—'} mono />
        <WlSpec label="Report" value={data.report?.id ? <Link className="underline" to={`/wireline-slickline/reports/${data.report.id}`} data-testid="wl-ai-report-link">Open the report</Link> : '—'} />
        <WlSpec label="Report status" value={text(data.report?.status)} />
      </dl>
      <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
        Approving writes this narrative into the report's summary, and only while the report is a draft.
      </p>
    </WlPanel>
  );
}

// ─── Compliance digest ───────────────────────────────────────────────────────

const stateCell = (s: string | null | undefined) => <WlBadge skin={wlAiStateSkin(s)} value={s ?? ''} />;
const jobsCell = (list: unknown) => {
  const j = arr<string>(list);
  return j.length ? j.join(', ') : '—';
};
const sourceLabel = (x: { isotope?: string | null; serialNumber?: string | null }) =>
  [x.isotope, x.serialNumber].filter((p) => typeof p === 'string' && p.trim()).join(' · ') || '—';
const CHECK_LABEL: Record<string, string> = { pull_test: 'Pull test', inspection: 'Inspection' };

export function ComplianceFigures({ data }: { data: WlComplianceDigestData }) {
  const crew = arr<WlComplianceDigestData['crewCerts'][number]>(data.crewCerts);
  const pc = arr<WlComplianceDigestData['pressureControl'][number]>(data.pressureControl);
  const stack = arr<WlComplianceDigestData['stackGuard'][number]>(data.stackGuard);
  const noStack = arr<WlComplianceDigestData['unitsWithoutStack'][number]>(data.unitsWithoutStack);
  const gauges = arr<WlComplianceDigestData['gauges'][number]>(data.gauges);
  const lines = arr<WlComplianceDigestData['lines'][number]>(data.lines);
  const leaks = arr<WlComplianceDigestData['leakTests'][number]>(data.leakTests);
  const out = arr<WlComplianceDigestData['sourcesOut'][number]>(data.sourcesOut);
  const lost = arr<WlComplianceDigestData['sourcesLost'][number]>(data.sourcesLost);
  const doses = arr<WlComplianceDigestData['doses'][number]>(data.doses);
  const unrecorded = crew.filter((x) => x.state === 'expiry_not_recorded').length;
  return (
    <div className="space-y-4" data-testid="wl-ai-compliance-figures">
      <WlPanel eyebrow="Compliance digest" title="Open wireline work">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3 lg:grid-cols-6">
          <WlSpec label="As of" value={wlDay(data.today)} />
          <WlSpec label="Items" value={displayValue(num(data.total))} mono />
          <WlSpec label="Crew certs" value={displayValue(crew.length)} mono />
          <WlSpec label="Pressure control" value={displayValue(pc.length)} mono />
          <WlSpec label="Stack check" value={displayValue(stack.length)} mono />
          <WlSpec label="Units without a stack" value={displayValue(noStack.length)} mono />
        </dl>
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Crew certificates" testId="wl-ai-certs">
        <AiTable
          rows={crew}
          testId="wl-ai-cert-item"
          rowKey={(x, i) => `${x.userId}:${x.cert}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="Everyone crewed on open work holds current required certificates."
          cols={[
            { key: 'name', header: 'Crew member', render: (x) => <span className="font-semibold text-white">{text(x.name)}</span> },
            { key: 'cert', header: 'Certificate', render: (x) => text(x.cert) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Expires', render: (x) => wlDay(x.dueOn) },
          ]}
        />
        {unrecorded > 0 && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted" data-testid="wl-ai-expiry-note">
            A certificate with its expiry date not recorded is a warning to fill the date in. It does not take anyone off a job.
          </p>
        )}
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Pressure control" testId="wl-ai-pressure-control">
        <AiTable
          rows={pc}
          testId="wl-ai-pc-item"
          rowKey={(x, i) => `${x.equipmentId}:${x.component ?? ''}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="No pressure-control component on an open job is expired, due or missing a certificate."
          cols={[
            { key: 'unit', header: 'Equipment', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'component', header: 'Component', render: (x) => text(x.component) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Due', render: (x) => wlDay(x.dueOn) },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Stack check" testId="wl-ai-stack-guard">
        <AiTable
          rows={stack}
          testId="wl-ai-stack-item"
          rowKey={(x, i) => `${x.equipmentId}:${x.jobNumber ?? ''}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="Every stack on an open job passes its check for the job's planned dates."
          cols={[
            { key: 'unit', header: 'Stack', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'job', header: 'Job', render: (x) => text(x.jobNumber) },
            { key: 'wlunit', header: 'Unit', render: (x) => text(x.unitNumber) },
            { key: 'end', header: 'Planned end', render: (x) => wlDay(x.plannedEndOn) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'why', header: 'Why', render: (x) => text(x.error) },
          ]}
        />
        {noStack.length > 0 && (
          <AiTable
            rows={noStack}
            testId="wl-ai-no-stack-item"
            rowKey={(x, i) => `${x.jobId}:${x.unitNumber ?? ''}:${i}`}
            rail={(x) => wlAiStateSkin(x.state).rail}
            empty=""
            minWidth="min-w-[420px]"
            cols={[
              { key: 'job', header: 'Job', render: (x) => (x.jobId ? <Link className="font-mono underline" to={`/wireline-slickline/jobs/${x.jobId}`}>{text(x.jobNumber)}</Link> : text(x.jobNumber)) },
              { key: 'wlunit', header: 'Unit', render: (x) => text(x.unitNumber) },
              { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            ]}
          />
        )}
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Lines and gauges" testId="wl-ai-lines">
        <AiTable
          rows={lines}
          testId="wl-ai-line-item"
          rowKey={(x, i) => `${x.lineId}:${x.check}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="No line is due a pull test or an inspection."
          minWidth="min-w-[480px]"
          cols={[
            { key: 'line', header: 'Line', render: (x) => (x.lineId ? <Link className="font-mono underline" to={`/wireline-slickline/lines/${x.lineId}`}>{text(x.lineNumber)}</Link> : text(x.lineNumber)) },
            { key: 'check', header: 'Check', render: (x) => CHECK_LABEL[x.check] ?? text(x.check) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Due', render: (x) => wlDay(x.dueOn) },
          ]}
        />
        {gauges.length > 0 && (
          <AiTable
            rows={gauges}
            testId="wl-ai-gauge-item"
            rowKey={(x, i) => `${x.equipmentId}:${i}`}
            rail={(x) => wlAiStateSkin(x.state).rail}
            empty=""
            minWidth="min-w-[420px]"
            cols={[
              { key: 'unit', header: 'Gauge', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
              { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
              { key: 'due', header: 'Due', render: (x) => wlDay(x.dueOn) },
            ]}
          />
        )}
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Radioactive sources" testId="wl-ai-sources">
        <AiTable
          rows={[...lost, ...out, ...leaks]}
          testId="wl-ai-source-item"
          rowKey={(x, i) => `${x.sourceId}:${x.state}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="No source is lost, out too long or due a leak test."
          minWidth="min-w-[480px]"
          cols={[
            { key: 'source', header: 'Source', render: (x) => <span className="font-semibold text-white">{sourceLabel(x)}</span> },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            {
              key: 'detail', header: 'Detail', render: (x) => {
                const o = x as WlComplianceDigestData['sourcesOut'][number];
                return typeof o.hoursOut === 'number' ? `${wlHours(o.hoursOut)} out (alert at ${wlHours(num(o.alertHours))})` : wlDay(x.dueOn);
              },
            },
          ]}
        />
      </WlPanel>
      <WlPanel eyebrow="Compliance" title="Dose" testId="wl-ai-doses">
        <AiTable
          rows={doses}
          testId="wl-ai-dose-item"
          rowKey={(x, i) => `${x.userId}:${x.periodStart ?? ''}:${i}`}
          rail={(x) => wlAiStateSkin(x.state).rail}
          empty="No one is over the dose level."
          minWidth="min-w-[560px]"
          cols={[
            { key: 'name', header: 'Crew member', render: (x) => <span className="font-semibold text-white">{text(x.name)}</span> },
            { key: 'period', header: 'Period', render: (x) => `${wlDay(x.periodStart)} – ${wlDay(x.periodEnd)}` },
            { key: 'dose', header: 'Deep dose', align: 'right', render: (x) => wlWithUnit(num(x.deepDoseMsv), 'mSv') },
            { key: 'level', header: 'Level', align: 'right', render: (x) => wlWithUnit(num(x.levelMsv), 'mSv') },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
          ]}
        />
      </WlPanel>
    </div>
  );
}

/** The figures for any run, by agent. Unknown agent or missing data says so rather than guessing. */
export function RunFigures({ agent, data }: { agent: string; data: unknown }) {
  if (!data || typeof data !== 'object') {
    return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">The figures for this run were not stored.</p>;
  }
  if (agent === 'wl_sheet_summary') return <SheetSummaryFigures data={data as WlSheetSummaryData} />;
  if (agent === 'wl_ticket_preflight') return <PreflightFigures data={data as WlPreflightData} />;
  if (agent === 'wl_service_report_draft') return <ReportDraftFigures data={data as WlReportDraftData} />;
  if (agent === 'wl_compliance_digest') return <ComplianceFigures data={data as WlComplianceDigestData} />;
  return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">This kind of draft is not shown here.</p>;
}

// ─── The AI draft and its review ─────────────────────────────────────────────

/** The heading says where the draft stands. A PENDING draft is waiting on a person and is not published. */
export const WL_DRAFT_HEADING: Record<string, string> = {
  pending: 'Draft — awaiting your approval',
  actioned: 'Approved',
  dismissed: 'Draft — dismissed',
  superseded: 'Draft — replaced by a newer run',
  no_action: 'Draft — no action',
};

function DraftText({ draft, testId }: { draft: WlAiDraft; testId: string }) {
  const obs = arr<string>(draft.observations);
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="whitespace-pre-wrap" data-testid={`${testId}-narrative`}>{text(draft.narrative)}</p>
      {obs.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" data-testid={`${testId}-observations`}>
          {obs.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A draft with no narrative text in it is absent, the same as a null draft. */
const hasNarrative = (d: WlAiDraft | null | undefined): d is WlAiDraft => !!d && typeof d.narrative === 'string' && d.narrative.trim() !== '';

/**
 * The AI's text. Approve / Edit / Dismiss only on a PENDING run and only for a
 * role that may review (the API refuses anyone else, and refuses a decided run
 * with 409 not_pending). An absent narrative is SAID to be absent, with the
 * API's reason, and a person may still write the narrative and approve that.
 */
export function WlAiDraftPanel({
  runId,
  outcome,
  draft: rawDraft,
  approved,
  approvedEdited,
  aiError,
  canReview,
}: {
  runId: string;
  outcome: string | null | undefined;
  draft: WlAiDraft | null | undefined;
  approved?: WlAiDraft | null;
  approvedEdited?: boolean | null;
  aiError: string | null | undefined;
  canReview: boolean;
}) {
  const review = useReviewWlAi();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [observations, setObservations] = useState('');
  const draft = hasNarrative(rawDraft) ? rawDraft : null;
  const pending = outcome === 'pending';
  const heading = (outcome && WL_DRAFT_HEADING[outcome]) || 'Draft';
  const busy = review.isPending;

  const startEdit = () => {
    setError(null);
    setNarrative(draft?.narrative ?? '');
    setObservations(arr<string>(draft?.observations).join('\n'));
    setEditing(true);
  };

  const approve = (edit?: { narrative: string; observations: string[] }) => {
    setError(null);
    review.mutate(
      { id: runId, action: 'approve', ...(edit ? { edit } : {}) },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft approved');
        },
        onError: (err) => setError(wlAiRefusal(err, 'The draft was not approved.')),
      },
    );
  };

  const dismiss = () => {
    setError(null);
    review.mutate(
      { id: runId, action: 'dismiss' },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft dismissed');
        },
        onError: (err) => setError(wlAiRefusal(err, 'The draft was not dismissed.')),
      },
    );
  };

  const submitEdit = () => {
    if (!narrative.trim()) {
      setError('Write the summary before approving it.');
      return;
    }
    approve({ narrative, observations: observations.split('\n').map((o) => o.trim()).filter(Boolean) });
  };

  return (
    <section data-testid="wl-ai-draft" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">AI draft</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white" data-testid="wl-ai-draft-heading">
            {heading}
          </h2>
        </div>
        {canReview && pending && !editing && (
          <div className="flex flex-wrap gap-2" data-testid="wl-ai-review-actions">
            <button type="button" className={WL_SECONDARY} onClick={dismiss} disabled={busy} data-testid="wl-ai-dismiss">
              <XCircle className="size-4" aria-hidden="true" />
              Dismiss
            </button>
            <button type="button" className={WL_SECONDARY} onClick={startEdit} disabled={busy} data-testid="wl-ai-edit">
              <Pencil className="size-4" aria-hidden="true" />
              {draft ? 'Edit, then approve' : 'Write and approve'}
            </button>
            {draft && (
              <button type="button" className={WL_PRIMARY} onClick={() => approve()} disabled={busy} data-testid="wl-ai-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve
              </button>
            )}
          </div>
        )}
      </header>
      {pending && (
        <p className="border-b border-fieldiq-border bg-fi-signal-amber/10 px-4 py-2 text-xs text-fieldiq-text" data-testid="wl-ai-not-published">
          Not approved yet. Nothing has been sent, filed or published — this draft waits for a person to approve or dismiss it.
        </p>
      )}
      {error && (
        <p role="alert" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="wl-ai-review-error">
          {error}
        </p>
      )}
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        {editing ? (
          <div className="space-y-3" data-testid="wl-ai-edit-form">
            <WlField label="Summary" hint={`Up to ${WL_AI_MAX_NARRATIVE} characters.`}>
              <textarea
                className={`${WL_INPUT} h-32 py-2`}
                aria-label="Summary"
                maxLength={WL_AI_MAX_NARRATIVE}
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                data-testid="wl-ai-edit-narrative"
              />
            </WlField>
            <WlField label="Observations" hint={`One per line; up to 8, each up to ${WL_AI_MAX_OBSERVATION} characters.`}>
              <textarea
                className={`${WL_INPUT} h-24 py-2`}
                aria-label="Observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                data-testid="wl-ai-edit-observations"
              />
            </WlField>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={WL_SECONDARY} onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className={WL_PRIMARY} onClick={submitEdit} disabled={busy} data-testid="wl-ai-edit-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve this text
              </button>
            </div>
          </div>
        ) : outcome === 'actioned' && hasNarrative(approved) ? (
          <>
            <DraftText draft={approved} testId="wl-ai-approved" />
            <p className="text-xs text-fieldiq-muted">{approvedEdited ? 'Edited by the reviewer before approval.' : 'Approved as the AI drafted it.'}</p>
          </>
        ) : draft ? (
          <>
            <DraftText draft={draft} testId="wl-ai-text" />
            <p className="text-xs text-fieldiq-muted">Written by AI from the figures above, as an explanation only. It changes no figure.</p>
          </>
        ) : (
          <div className="space-y-1" data-testid="wl-ai-no-draft">
            <p className="font-bold">AI narrative absent — the figures above are still correct.</p>
            <p className="text-xs text-fieldiq-muted" data-testid="wl-ai-error">
              {aiError ? `Reason: ${aiError}` : 'The API gave no reason.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Launchers: "Draft with AI" on the day sheet, ticket, job and report pages ────

/**
 * A run just made, shown in place. The POST answers without an outcome, so the
 * stored run is read for it (and re-read after a review); until that loads, a
 * fresh run is pending by definition.
 */
export function WlAiRunInPlace({ result, canReview }: { result: WlAiRunResult; canReview: boolean }) {
  const stored = useWlAiDigest(result.agentRunId);
  const s = stored.data;
  return (
    <div className="space-y-4" data-testid="wl-ai-in-place">
      <RunFigures agent={String(result.agent)} data={result.data} />
      <WlAiDraftPanel
        runId={result.agentRunId}
        outcome={s?.outcome ?? 'pending'}
        draft={s ? s.draft : result.draft}
        approved={s?.approved ?? null}
        approvedEdited={s?.approvedEdited ?? null}
        aiError={s ? s.aiError : result.aiError}
        canReview={canReview}
      />
      <p className="text-xs text-fieldiq-muted">
        Kept with the{' '}
        <Link to={`/wireline-slickline/ai/${result.agentRunId}`} className="font-bold text-fieldiq-text underline" data-testid="wl-ai-run-link">
          AI drafts
        </Link>
        .
      </p>
    </div>
  );
}

/**
 * One "Draft with AI" panel: a button that runs `request`, a refusal in the
 * API's words (a 409 report_issued as a sentence), and the run shown in place.
 * The caller decides whether to render it at all — by role (canUseWlAi) and by
 * the record's state — so a hand who may not call it never sees the button.
 */
export function WlAiLauncher({
  request,
  eyebrow,
  title,
  blurb,
  testId,
}: {
  request: WlAiRunRequest;
  eyebrow: string;
  title: string;
  blurb: string;
  testId: string;
}) {
  const run = useRunWlAi();
  const [result, setResult] = useState<WlAiRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    run.mutate(request, {
      onSuccess: (r) => setResult(r ?? null),
      onError: (err) => setError(wlAiRefusal(err, 'The AI draft did not run.')),
    });
  };
  return (
    <div className="space-y-4" data-testid={testId}>
      <WlPanel
        eyebrow={eyebrow}
        title={title}
        actions={
          <button type="button" className={WL_SECONDARY} onClick={go} disabled={run.isPending} data-testid={`${testId}-run`}>
            {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Sparkles className="size-4" aria-hidden="true" />}
            {run.isPending ? 'Drafting…' : 'Draft with AI'}
          </button>
        }
      >
        <div className="space-y-2 px-4 py-4">
          <WlFormError message={error} />
          <p className="text-xs text-fieldiq-muted">{blurb}</p>
        </div>
      </WlPanel>
      {result && <WlAiRunInPlace result={result} canReview />}
    </div>
  );
}
